<script lang="ts">
  import Nav from './lib/components/Nav.svelte'
  import Footer from './lib/components/Footer.svelte'
  import { Router } from './lib/router.svelte'
  import { ROUTES, NOT_FOUND } from './routes'

  const router = new Router(ROUTES, NOT_FOUND)

  /**
   * The outlet component, read HERE and not as an `{@const}` inside the
   * `{#if}` below.
   *
   * `{@const}` compiles to a derived owned by the if-branch, and its only
   * consumer is the dynamic-component effect inside the `{#key}` block. The
   * first time that block is torn down the derived loses its consumer and
   * stops tracking `router.component`, so the outlet kept whatever component
   * it already had: every navigation rendered the PREVIOUS page under the new
   * URL, and the page you asked for only appeared on the click after it.
   * A derived at component scope is owned by the component and survives the
   * key block's teardown.
   */
  const Page = $derived(router.component)

  // No session fetch and no auth guard: the shop is not open, so there is
  // nothing to be signed in to. Removing the /me request also means a cold
  // load makes ZERO network calls beyond the page and its assets.
  void router.resolve()
</script>

<Nav {router} />

<main id="main" class:is-navigating={router.navigating}>
  {#if Page}
    <!-- Keying forces a fresh instance per navigation, so page state (filters,
         forms) never leaks from one route into the next. The key is the
         navigation counter and NOT `router.path`: the path is published the
         moment a link is clicked, so keying on it re-mounts the OUTGOING page
         under the new URL for as long as the new chunk is in flight. The
         counter ticks when the chunk lands, so the swap happens once. -->
    {#key router.view}
      <Page params={router.params} {router} />
    {/key}
  {:else}
    <div class="boot" role="status" aria-live="polite">
      <span class="visually-hidden">Loading</span>
    </div>
  {/if}
</main>

<Footer />

<style>
  /* Deliberately NOT a flex column. A flex item defaults to min-width:auto,
     which means a section containing a wide table or code block refuses to
     shrink and pushes the whole page into horizontal scroll — the table's own
     `overflow-x: auto` never gets a chance to do its job. `body` is the flex
     column that keeps the footer down; `main` only needs to be a block. */
  main {
    display: block;
    min-height: 60vh;
    transition: opacity 0.12s ease;
  }
  /* Only dim on a slow chunk; an instant navigation must not flicker. */
  main.is-navigating { opacity: 0.72; }

  .boot { min-height: 60vh; }

  :global(html), :global(body) { min-height: 100%; }
  :global(body) { display: flex; flex-direction: column; min-height: 100dvh; }
</style>
